import { Input } from "@/components/ui/input";
import { Plus, Trash2 } from "lucide-react";

const EXAMPLE = [4.5, 3, 0, -2];

const chanceFor = (diff, table) => {
  let pick = table[0]?.chance ?? 0;
  [...table].sort((a, b) => a.diff - b.diff).forEach((r) => { if (r.diff <= diff + 1e-9) pick = r.chance; });
  return pick;
};

const Example = ({ table, market }) => {
  const top = Math.max(...EXAMPLE);
  const rows = EXAMPLE.map((raw) => ({ raw, diff: top - raw, chance: chanceFor(top - raw, table) }));
  const sum = rows.reduce((s, r) => s + r.chance, 0);
  return (
    <div className="panel p-5">
      <div className="eyebrow mb-3">Live Example (4-runner field)</div>
      <table className="w-full text-sm num" data-testid="formula-example">
        <thead><tr className="eyebrow text-left">{["Raw", "Rating", "Diff", "Chance", "%", "$ Price"].map((h) => <th key={h} className="py-1 font-normal text-right first:text-left">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => {
            const pct = (r.chance / sum) * 100;
            return (
              <tr key={r.raw} className="border-t border-slate-800">
                <td className="py-2">{r.raw.toFixed(1)}</td>
                <td className="py-2 text-right text-emerald-400">{(100 - r.diff).toFixed(1)}</td>
                <td className="py-2 text-right">{r.diff.toFixed(1)}</td>
                <td className="py-2 text-right">{r.chance}</td>
                <td className="py-2 text-right">{pct.toFixed(1)}%</td>
                <td className="py-2 text-right text-amber-400 font-bold">${(Math.floor((1000 / ((pct * market) / 100)) + 0.5) / 10).toFixed(2)}</td>
              </tr>
            );
          })}
          <tr className="border-t border-slate-700 text-slate-400"><td colSpan={3} className="py-2">Sum of chance</td><td className="py-2 text-right">{sum}</td><td colSpan={2} /></tr>
        </tbody>
      </table>
    </div>
  );
};

export const FormulaEditor = ({ formula, onChange }) => {
  const table = formula.chance_table || [];
  const setRow = (i, k, v) => onChange({ ...formula, chance_table: table.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  const addRow = () => {
    const last = table[table.length - 1] || { diff: -0.5, chance: 1 };
    onChange({ ...formula, chance_table: [...table, { diff: last.diff + 0.5, chance: last.chance }] });
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="formula-editor">
      <div className="space-y-6">
        <div className="bg-[#0F172A] border border-slate-800 rounded p-4 num text-xs text-slate-300 space-y-1.5">
          <div><span className="text-slate-500">Rating</span> = 100 − Diff (Diff = top Raw Score − Raw Score)</div>
          <div><span className="text-slate-500">Chance</span> = Diff looked up in the table below</div>
          <div><span className="text-slate-500">% chance</span> = Chance ÷ Σ Chance of the field × 100</div>
          <div><span className="text-slate-500">$ Price</span> = 100 ÷ % chance, rounded to nearest $0.10{formula.market_pct !== 100 && ` (framed to ${formula.market_pct}%)`}</div>
        </div>
        <div className="panel p-4">
          <div className="eyebrow mb-2">Difference → Chance Table</div>
          <div className="max-h-[420px] overflow-y-auto pr-1">
            <table className="w-full text-sm" data-testid="chance-table">
              <thead><tr className="eyebrow text-left"><th className="py-1 font-normal">Difference</th><th className="py-1 font-normal">Chance</th><th /></tr></thead>
              <tbody>
                {table.map((r, i) => (
                  <tr key={i}>
                    <td className="py-0.5 pr-2"><Input data-testid={`chance-diff-${i}`} type="number" step="0.5" value={r.diff} onChange={(e) => setRow(i, "diff", Number(e.target.value))} className="h-8 bg-[#0F172A] num" /></td>
                    <td className="py-0.5 pr-2"><Input data-testid={`chance-value-${i}`} type="number" step="0.05" value={r.chance} onChange={(e) => setRow(i, "chance", Number(e.target.value))} className="h-8 bg-[#0F172A] num" /></td>
                    <td className="py-0.5 w-8">
                      <button data-testid={`chance-remove-${i}`} onClick={() => onChange({ ...formula, chance_table: table.filter((_, j) => j !== i) })} className="text-slate-500 hover:text-rose-400 transition-colors"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button data-testid="chance-add-row" onClick={addRow} className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 transition-colors"><Plus className="h-3.5 w-3.5" /> Add row</button>
          <p className="text-xs text-slate-500 mt-2">Gaps beyond the last row use the last row's chance.</p>
        </div>
        <label className="block">
          <span className="eyebrow">Market Framing %</span>
          <Input data-testid="formula-market-pct-input" type="number" step="1" value={formula.market_pct} onChange={(e) => onChange({ ...formula, market_pct: Number(e.target.value) })} className="mt-1.5 bg-[#0F172A] num w-40" />
          <p className="text-xs text-slate-500 mt-2">100 = straight 100 ÷ % prices (your formula).</p>
        </label>
      </div>
      <Example table={table} market={formula.market_pct || 100} />
    </div>
  );
};
